// src/services/dashboardService.js
// Dashboard-specific data fetching service

import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs
} from "firebase/firestore";
import { db } from "../firebase/config";
import { getRunSummary } from "./payrollRunStore";
import { currentPeriod, DEFAULT_CUTOFF_START_DAYS } from "../utils/payCutoffs";
import { parsePeriodToDate, toYMDString } from "../utils/periodDate";

class DashboardService {
  /**
   * Get today's time records summary for all employees
   * @param {string} companyId
   * @returns {Object} { employeeIdsWithRecords: Set, recordCount: number, date: string }
   */
  async getTodayTimeRecordsSummary(companyId) {
    try {
      const today = this.formatDate(new Date());

      const timeRecordsRef = collection(db, "companies", companyId, "timeRecords");
      const q = query(
        timeRecordsRef,
        where("date", "==", today)
      );

      const snapshot = await getDocs(q);
      const employeeIdsWithRecords = new Set();

      snapshot.forEach(doc => {
        const record = doc.data();
        // Count employee if they have at least a timeIn entry
        if (record.actualTimeIn) {
          employeeIdsWithRecords.add(record.employeeId);
        }
      });

      return {
        employeeIdsWithRecords,
        recordCount: employeeIdsWithRecords.size,
        date: today
      };
    } catch (error) {
      console.error("Error fetching today's time records:", error);
      return {
        employeeIdsWithRecords: new Set(),
        recordCount: 0,
        date: this.formatDate(new Date())
      };
    }
  }

  /**
   * Get completed payroll runs for dashboard
   * @param {string} companyId
   * @param {number} maxResults
   * @returns {Array} List of completed payroll runs
   */
  async getCompletedPayrollRuns(companyId, maxResults = 5) {
    try {
      const runsRef = collection(db, "companies", companyId, "payrollRuns");
      const q = query(
        runsRef,
        where("status", "==", "completed"),
        orderBy("lastModified", "desc"),
        limit(maxResults)
      );

      const snapshot = await getDocs(q);
      const runs = [];

      snapshot.forEach(doc => {
        const data = doc.data();
        // Prefer the denormalized parent aggregates (C2); historical completed
        // runs fall back to the inline employees — both via the shared helper.
        const summary = getRunSummary(data);
        runs.push({
          id: doc.id,
          runId: data.runId,
          periodStart: data.periodStart?.toDate ? data.periodStart.toDate() : new Date(data.periodStart),
          periodEnd: data.periodEnd?.toDate ? data.periodEnd.toDate() : new Date(data.periodEnd),
          lastModified: data.lastModified?.toDate ? data.lastModified.toDate() : null,
          status: data.status,
          summary
        });
      });

      return runs;
    } catch (error) {
      console.error("Error fetching payroll runs:", error);
      return [];
    }
  }

  /**
   * Get current pay period dates from the company's tiled cutoffs
   * (C2 — settings/payroll.cutoffStartDays; the default preserves the
   * classic 1-15 / 16-end calendar).
   * @param {Array<number>} cutoffStartDays - the two cutoff start days
   * @returns {Object} { start: Date, end: Date }
   */
  getCurrentPayPeriod(cutoffStartDays = DEFAULT_CUTOFF_START_DAYS) {
    const period = currentPeriod(cutoffStartDays, toYMDString(new Date()));
    return {
      start: parsePeriodToDate(period.start),
      end: parsePeriodToDate(period.end),
    };
  }

  /**
   * Calculate estimated gross payroll for current period
   * @param {Array} employees - Active employees
   * @returns {number} Estimated gross amount
   */
  calculateEstimatedGross(employees) {
    if (!employees || employees.length === 0) return 0;

    let totalEstimate = 0;

    employees.forEach(employee => {
      const monthlySalary = parseFloat(employee.employment?.monthlySalary) || 0;
      // ÷2 = the fixed semi-monthly base (M2) — C7 fixes cyclesPerMonth at 2
      // for every supported pay calendar, so this holds for custom cutoffs too.
      totalEstimate += monthlySalary / 2;
    });

    return totalEstimate;
  }

  /**
   * Calculate department breakdown from employees
   * @param {Array} employees
   * @returns {Object} { departmentName: { count, percentage, color } }
   */
  calculateDepartmentBreakdown(employees) {
    if (!employees || employees.length === 0) {
      return {};
    }

    const departments = {};
    const total = employees.length;

    // Department segment colours for the Workforce donut and its legend.
    //
    // These are CSS custom-property references, not hexes, because the value is
    // painted straight into a conic-gradient (Dashboard.js) and a legend swatch
    // (WorkforceCard.js) — both of which resolve var() fine; Dashboard.js's own
    // empty-chart fallback has always been a var(). This file was missed by the
    // emerald sweep (it swept src/styles and src/components, never src/services),
    // so the donut was still painting the Philippine flag on an emerald page.
    //
    // Chosen for separability, not meaning: no department "is" a warning, so the
    // ramp walks emerald → amber → deep emerald → body → faint → ink rather than
    // reusing status roles. --ui-success and --ui-warning are deliberately absent
    // — post-flip they resolve to the same emerald and amber as --ui-primary and
    // --ui-accent, so two departments would render identically.
    const colorPalette = [
      "var(--ui-primary)",
      "var(--ui-accent)",
      "var(--ui-primary-hover)",
      "var(--ui-text-secondary)",
      "var(--ui-text-faint)",
      "var(--ui-surface-dark)",
    ];
    let colorIndex = 0;

    employees.forEach(employee => {
      const dept = employee.employment?.department || "Unassigned";
      if (!departments[dept]) {
        departments[dept] = {
          count: 0,
          color: colorPalette[colorIndex % colorPalette.length]
        };
        colorIndex++;
      }
      departments[dept].count++;
    });

    // Calculate percentages
    Object.keys(departments).forEach(dept => {
      departments[dept].percentage = Math.round((departments[dept].count / total) * 100);
    });

    return departments;
  }

  /**
   * Format date to YYYY-MM-DD
   */
  formatDate(date) {
    const d = new Date(date);
    const year = d.getFullYear();
    const month = String(d.getMonth() + 1).padStart(2, "0");
    const day = String(d.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  }

  /**
   * Format currency in Philippine Peso (compact for large numbers)
   */
  formatCurrency(amount, compact = false) {
    if (compact && amount >= 1000000) {
      return `₱${(amount / 1000000).toFixed(1)}M`;
    } else if (compact && amount >= 1000) {
      return `₱${Math.round(amount / 1000)}K`;
    }

    return new Intl.NumberFormat("en-PH", {
      style: "currency",
      currency: "PHP",
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(amount || 0).replace("PHP", "₱");
  }

  /**
   * Format period display string (e.g., "Dec 1 - 15, 2024")
   */
  formatPeriod(start, end) {
    const options = { month: "short", day: "numeric" };
    const startStr = new Intl.DateTimeFormat("en-US", options).format(start);
    const endStr = new Intl.DateTimeFormat("en-US", options).format(end);
    const year = new Intl.DateTimeFormat("en-US", { year: "numeric" }).format(end);
    return `${startStr} - ${endStr}, ${year}`;
  }

  /**
   * Format date for display — 'YYYY-MM-DD', the app-wide standard
   * (DESIGN-STANDARDS.md §13). Was "December 26, 2024".
   *
   * Renders as the "Today: …" value on the dashboard's Time Records card,
   * which is a labelled value rather than prose, so §13 covers it.
   */
  formatDisplayDate(date) {
    return toYMDString(date);
  }

  /**
   * Format short date (e.g., "Dec 26")
   */
  formatShortDate(date) {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric"
    }).format(date);
  }

  /**
   * Format date with year (e.g., "Dec 31, 2025")
   */
  formatDateWithYear(date) {
    return new Intl.DateTimeFormat("en-US", {
      month: "short",
      day: "numeric",
      year: "numeric"
    }).format(date);
  }

  /**
   * Get day of week name
   */
  getDayOfWeek(date) {
    return new Intl.DateTimeFormat("en-US", { weekday: "long" }).format(date);
  }
}

export const dashboardService = new DashboardService();
